import React from 'react';
import { format } from 'date-fns';
import { Globe, Edit, Trash2, Building2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Company } from '@/lib/types';
import { WebSearchStatus } from '@/services/companyWebSearchService';

interface CompanyCardProps {
  company: Company;
  isAdmin: boolean;
  webSearchStatus: WebSearchStatus;
  onView: (companyId: string) => void;
  onEdit?: (companyId: string) => void;
  onDelete?: (companyId: string, companyName: string) => void;
}

const CompanyCard: React.FC<CompanyCardProps> = ({
  company,
  isAdmin,
  webSearchStatus,
  onView,
  onEdit,
  onDelete,
}) => {
  const renderWebSearchStatus = () => {
    switch (webSearchStatus) {
      case 'completed':
        return <span className="text-green-600 font-medium">Completed</span>;
      case 'pending':
        return <span className="text-amber-600 font-medium">Pending</span>;
      case 'processing':
        return <span className="text-blue-600 font-medium">Processing</span>;
      case 'failed':
        return <span className="text-red-600 font-medium">Failed</span>;
      default:
        return <span className="text-muted-foreground">Not started</span>;
    }
  };

  return (
    <Card 
      className="hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => onView(company.id)}
    >
      <CardContent className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Building2 className="h-5 w-5 text-primary flex-shrink-0" />
            <h3 className="font-semibold text-base line-clamp-1">{company.name}</h3>
          </div>
          {company.website && (
            <a
              href={company.website}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-blue-500 hover:text-blue-600 flex-shrink-0"
            >
              <Globe className="h-4 w-4" />
            </a>
          )}
        </div>

        {/* Industry */}
        {company.industry && (
          <Badge variant="outline" className="w-fit">
            {company.industry}
          </Badge>
        )}

        {/* Company Details */}
        <div className="space-y-2 text-sm">
          {company.size && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Size:</span>
              <Badge variant="secondary">{company.size}</Badge>
            </div>
          )}

          {company.website && (
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <a
                href={company.website}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-blue-500 hover:underline line-clamp-1"
              >
                {company.website}
              </a>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-blue-500 flex-shrink-0" />
            <span className="text-muted-foreground">Web Search:</span>
            {renderWebSearchStatus()}
          </div>

          <div className="text-muted-foreground">
            Created: {format(new Date(company.created_at), "PPP")}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2 border-t">
          {isAdmin ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit?.(company.id);
                }}
                className="flex-1"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete?.(company.id, company.name);
                }}
                className="flex-1"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onView(company.id);
              }}
              className="w-full"
            >
              View Details
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CompanyCard;
